import {
  TStoreApi,
  TContainerProps,
  IFile,
  TExportResponse,
} from '../common/types';
import { setAsyncInterval, clearAsyncInterval, normalizeMediaItem } from './utils';

const setLoading = (status: boolean) => ({ setState }: TStoreApi): void => {
  setState((draft) => {
    draft.loading = status;
  });
};

const setProgress = (progress: number, file: IFile) => ({
  setState,
}: TStoreApi): void => {
  setState((draft) => {
    draft.progressingFiles[file.name] = progress;
  });
};

const setExporting = (status: boolean) => ({ setState }: TStoreApi): void => {
  setState((draft) => {
    draft.exporting = status;
  });
};

const setImporting = (status: boolean) => ({ setState }: TStoreApi): void => {
  setState((draft) => {
    draft.importing = status;
  });
};

const setError = (error: string) => ({ setState }: TStoreApi): void => {
  setState((draft) => {
    draft.error = error;
  });
};


const INTERVAL_MS = 500;
const BRAND_KIT_ACCEPTED_FILETYPES = ['.kit']

export const actions = {
  load: () => async (
    { setState, dispatch }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    dispatch(setLoading(true));

    const { data } = await api.get('/api/v1/brands/media')
    setState((draft) => {
      draft.mediaItems = data;
    });

    dispatch(setLoading(false));
  },
  addColor: (color: string) => async (
    { setState }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('data-value', color);
    formData.append('data-desc', 'ColorName');
    formData.append('data-type', 'color');

    const { data } = await api.post(`/api/v1/brands/media`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
    setState((draft) => {
      draft.mediaItems = [...draft.mediaItems, normalizeMediaItem(data) ];
    });
  },
  removeMedia: (uuid: string) => async (
    { setState }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    await api.delete(`/api/v1/brands/media/${uuid}`);
    setState((draft) => {
      draft.mediaItems = draft.mediaItems.filter(
        (media) => media.uuid !== uuid
      );
    });
  },
  setFontName: (name: string, uuid: string) => async (
    { setState }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('data-display-name', name);

    await api.patch(`/api/v1/brands/media/${uuid}`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    });
    setState((draft) => {
      const updatedFont = draft.mediaItems.find((media) => media.uuid === uuid);
      if (updatedFont?.display_name) {
        // eslint-disable-next-line @typescript-eslint/camelcase
        updatedFont.display_name = name;
      }
    });
  },
  setAcceptedFiles: (files: IFile[]) => ({ setState }: TStoreApi): void => {
    setState((draft) => {
      draft.acceptedFiles = files;
    });
  },
  addFile: (file: IFile, fileType: 'font' | 'image' | 'logo') => async (
    { setState, dispatch }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('data-file', file);
    formData.append('data-type', fileType);

    const { data } = await api.post(`/api/v1/brands/media`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        dispatch(setProgress(percentCompleted, file));
      },
    });

    setState((draft) => {
      draft.mediaItems = [...draft.mediaItems, normalizeMediaItem(data) ];
    });
  },
  resetUploadState: () => ({ dispatch }: TStoreApi): void => {
    dispatch(actions.setAcceptedFiles([]));
  },

  /**
   * Initiates the export process. Request an export URL with a unique ID
   */
  getExportURL: () => async (
    { setState }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    setExporting(true);
    const { data } = await api.get(`/api/v1/brands/assets/export`);
    setState((draft) => {
      if (data.status === 'created') {
        draft.exportURL = data?.link;
        draft.exporting = true;
      }
    });
  },


  /**
   * Requests an export job to start.
   * When ready, the downloadUrl is passed back to the UI, file ready to be downloaded.
   */
  exportBrandKit: (apiURL: string) => async (
    { setState }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    let data: TExportResponse | null = null;
    const intervalIndex = setAsyncInterval(async (): Promise<void> => {
      ({ data } = await api.get(apiURL));
      if (data?.status === 'ready') {
        clearAsyncInterval(intervalIndex);
        setState((draft) => {
          draft.downloadURL = data?.link ?? '';
        });
      }
    }, INTERVAL_MS);
  },

  finishExport: () => ({ setState, dispatch }: TStoreApi): void => {
    setExporting(false);
    setState((draft) => {
      draft.exportURL = '';
      draft.downloadURL = '';
      draft.exporting = false;
    });
    dispatch(actions.closeImportExportModal());
  },

  getImportBrandKitUrl: () => async (
    { setState }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    const { data } = await api.post(`/api/v1/brands/assets/import`);
    setState((draft) => {
      if (data.status === 'created') {
        draft.importURL = data?.link;
        draft.importId = data.uuid;
      }
    });
  },

  importBrandKit: (file: IFile, apiURL: string) => async (
    { getState, setState, dispatch }: TStoreApi,
    { api }: TContainerProps
  ): Promise<void> => {
    try {
      if (!apiURL) {
        throw new Error('No import URL provided.');
      }

      // TODO: Possibly extract this filename validation logic to a separate function
      // Then apply it to all types of uploads, not just the brand kit import
      if (!file.name) {
        throw new Error('File must have name');
      }

      if (file.name.length >= 100) {
        throw new Error('Filename must be less than 100 characters');
      }

      // Matches something like ".kit", ".env", ".example" etc. at end of string
      const extensionMatch: (RegExpMatchArray | null) = file.name.match(/\.[^.]+$/);
      const extension: string = extensionMatch?.[0] || '';
      if (!BRAND_KIT_ACCEPTED_FILETYPES.includes(extension)) {
        throw new Error(`Incorrect file type, please try again with ${BRAND_KIT_ACCEPTED_FILETYPES.join(', ')} file type`);
      }

      const nameOnly: string = file.name.slice(0, file.name.length - extension.length);
      const hasAlphanumeric: (RegExpMatchArray | null) = nameOnly.match(/[A-Za-z\d]/);
      if (!hasAlphanumeric) {
        throw new Error('File must have name');
      }
    } catch (err) {
      dispatch(setError(err.message));
      return;
    }

    dispatch(setImporting(true));
    dispatch(setError(''));

    // 1. Upload file to the CDN
    const response = await api.put(
      apiURL,
      file,
      {
        headers: {
          'Content-Type': 'application/octet-stream',
          'Access-Control-Allow-Methods': 'PUT',
        }
      }
    );

    if (response.status === 200) {
      // 2. Check for import status in the Unfold API
      // add call to Unfold API to check if import is done.
      const { importId } = getState();
      if (importId !== '') {
        const intervalIndex = setAsyncInterval(async (): Promise<void> => {
          const { data } = await api.get(`/api/v1/brands/assets/import/${importId}`)
          if (data.status === 'completed') {
            clearAsyncInterval(intervalIndex);
            setState((draft) => {
              draft.importURL = '';
              draft.importId = '';
            });
            dispatch(actions.load());
            dispatch(actions.closeImportExportModal());
          }
        }, INTERVAL_MS);
      } else {
        throw new Error('No import ID provided.');
      }

      // Reload the brand kit to update the UI with newly imported items.
    } else {
      // error
      dispatch(setError('Something went wrong. Please try importing your content again'));
    }
    dispatch(setImporting(false));
  },

  openImportExportModal: () => (
    { setState }: TStoreApi,
  ): void => {
    setState((draft) => {
      draft.isImportExportModalOpen = true;
    });
  },

  closeImportExportModal: () => (
    { setState }: TStoreApi,
  ): void => {
    setState((draft) => {
      draft.isImportExportModalOpen = false;
    });
  }
};

export type TActions = typeof actions;
